import ReactQuill from "react-quill-new";
import ImageResize from "quill-image-resize-module-react";
import type QuillInstance from "quill";
import type { Range as QuillRange } from "quill";
import type Toolbar from "quill/modules/toolbar";
import type QuillTable from "quill/modules/table";

ReactQuill.Quill.register("modules/resize", ImageResize);

const Parchment = ReactQuill.Quill.import("parchment");
// ============================================================
// Alias for quill-image-resize-module-react.
// Needed because lib is deprecated.
// ============================================================
if (!("Style" in Parchment.Attributor)) {
    (Parchment.Attributor as any).Style = Parchment.StyleAttributor;
}

// ============================================================
// Override Image blot (react-quill-new) and specifying the whitelist for attributes (e.g. style, class).
// This is needed for proper image positioning and/or custom classes.
// Using ts-ignore due to lack of types
// ============================================================
const OldImageBlot = ReactQuill.Quill.import("formats/image");
//@ts-ignore
class ImageBlot extends OldImageBlot {
    static get ATTRIBUTES() {
        return ["alt", "height", "width", "class", "style"];
    }
    static formats(domNode: Element) {
        return ImageBlot.ATTRIBUTES.reduce((formats, attribute) => {
            if (domNode.hasAttribute(attribute)) {
                formats[attribute] = domNode.getAttribute(attribute);
            }
            return formats;
        }, {} as Record<string, string | null>);
    }
    format(name: string, value: string) {
        if (ImageBlot.ATTRIBUTES.indexOf(name) > -1) {
            if (value) {
                //@ts-ignore
                this.domNode.setAttribute(name, value);
            } else {
                //@ts-ignore
                this.domNode.removeAttribute(name);
            }
        } else {
            super.format(name, value);
        }
    }
}
//@ts-ignore
ReactQuill.Quill.register(ImageBlot, true);

// ============================================================
// Table toolbar button integration.
// The toolbar handler stays lightweight: it only inspects whether the
// current selection is inside a table, then notifies the React layer
// (see QuillInput) via a CustomEvent dispatched on the Quill root.
// All table popover UI/state is owned by React, not by this config.
// ============================================================
export const QUILL_TABLE_TOOLBAR_EVENT = "quill-table-toolbar-click";

export type { QuillInstance, QuillRange };
export type QuillTableModule = QuillTable;

export interface QuillTableToolbarEventDetail {
    quill: QuillInstance;
    tableModule: QuillTableModule;
    isInsideTable: boolean;
    anchorRect: DOMRect | null;
    savedRange: QuillRange | null;
}

// ============================================================
// Define all Quill modules we will use:
// - toolbar: editor toolbar with custom table handler
// - table: table support
// - resize: image resize settings.
// Specify which formats Quill should support in the editor.
// ============================================================
const quillModules = {
    toolbar: {
        container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
            [
                { list: "ordered" },
                { list: "bullet" },
                { indent: "-1" },
                { indent: "+1" },
            ],
            [{ script: "sub" }, { script: "super" }],
            ["link", "image", "video", "table"],
            [{ color: [] as any }, { background: [] as any }],
            [{ font: [] as any }],
            [{ align: [] as any }],
            ["clean"],
        ],
        handlers: {
            // Lightweight by design: only resolves context and hands off to React (QuillInput),
            // which owns the table popover UI/state and performs the actual table operations.
            table: function (this: Toolbar) {
                const quill = this.quill;
                const tableModule = quill.getModule("table") as QuillTableModule | undefined;
                if (!tableModule) return;

                const savedRange = quill.getSelection();
                const [table] = tableModule.getTable(savedRange ?? undefined);
                const buttonEl = this.container?.querySelector<HTMLElement>(".ql-table") ?? null;

                const detail: QuillTableToolbarEventDetail = {
                    quill,
                    tableModule,
                    isInsideTable: Boolean(table),
                    anchorRect: buttonEl?.getBoundingClientRect() ?? null,
                    savedRange,
                };

                quill.root.dispatchEvent(
                    new CustomEvent<QuillTableToolbarEventDetail>(QUILL_TABLE_TOOLBAR_EVENT, {
                        bubbles: true,
                        detail,
                    })
                );
            },
        },
    },
    table: true,
    resize: {
        parchment: Parchment,
        modules: ["Resize", "DisplaySize", "Toolbar"],
    },
};

const quillFormats = [
    "font",
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "background",
    "align",
    "code-block",
    "formula",
    "script",
    "table",
];

export { quillFormats, quillModules };
